<!-- Page header sits on the background, mirroring the faqs /
     placements / dozenten convention — `.header` + inner `<div>` +
     `<h2>` + `<p class="subtitle">`. Refresh action stays on the
     right side, same shape as faqs' filter dropdown. -->
<div class="header">
  <div>
    <h2>{{ 'pages.moodle.title' | translate }}</h2>
    <p class="subtitle">{{ 'pages.moodle.hint' | translate }}</p>
  </div>
  <div class="moodle-dashboard__header-actions">
    <nb-select
      *ngIf="canSelectMoodleSite && moodleSites.length > 1"
      size="small"
      [selected]="selectedMoodleRegistrationId"
      (selectedChange)="onMoodleSiteChange($event)"
      [placeholder]="'pages.moodle.siteFilterPlaceholder' | translate"
    >
      <nb-option *ngFor="let site of moodleSites" [value]="site.id">
        {{ site.display_name || site.moodle_site_url }}
        <span *ngIf="site.is_primary"> · {{ 'pages.moodle.primarySite' | translate }}</span>
      </nb-option>
    </nb-select>
    <button
      nbButton
      status="primary"
      outline
      class="secondary-action-button"
      (click)="refreshFromMoodle()"
      [disabled]="loading || refreshingFromMoodle"
    >
      <nb-icon
        [icon]="refreshingFromMoodle ? 'sync-outline' : 'refresh-outline'"
        [class.spin]="refreshingFromMoodle"
      ></nb-icon>
      {{ (refreshingFromMoodle ? 'pages.moodle.refreshing' : 'pages.moodle.refresh') | translate }}
    </button>
  </div>
</div>

<div *ngIf="refreshProgress" class="moodle-dashboard__refresh-progress">
  <div class="moodle-dashboard__refresh-progress-header">
    <nb-icon
      [icon]="refreshingFromMoodle ? 'sync-outline' : 'checkmark-circle-2-outline'"
      [class.spin]="refreshingFromMoodle"
    ></nb-icon>
    <span>{{ refreshProgressLabel }}</span>
  </div>
  <nb-progress-bar
    [status]="refreshProgressStatus"
    [value]="refreshProgressPercent"
  ></nb-progress-bar>
</div>

<nb-card class="moodle-dashboard">
  <nb-card-body>
    <nb-tabset (changeTab)="onTabChange($event.tabId)">
      <!-- ─── Tab: Kurse ─────────────────────────────────────── -->
      <nb-tab tabId="courses" [tabTitle]="'pages.moodle.tabs.courses' | translate">
        <div class="moodle-dashboard__filters">
          <nb-form-field class="moodle-dashboard__filter">
            <nb-icon nbPrefix icon="search-outline"></nb-icon>
            <input
              type="text"
              nbInput
              fieldSize="small"
              [(ngModel)]="searchCoursesInput"
              (keyup.enter)="applySearch('courses')"
              [placeholder]="'pages.moodle.searchCourse' | translate"
            />
            <nb-icon
              *ngIf="searchCoursesInput"
              nbSuffix
              icon="close-outline"
              class="moodle-dashboard__clear-search"
              (click)="clearSearch('courses')"
              [attr.title]="'pages.moodle.clearSearch' | translate"
            ></nb-icon>
          </nb-form-field>
          <nb-select
            [(ngModel)]="categoryFilter"
            (selectedChange)="onCourseFilterChange()"
            size="small"
            [placeholder]="'pages.moodle.filters.category' | translate"
          >
            <nb-option [value]="null">{{ 'pages.moodle.filters.allCategories' | translate }}</nb-option>
            <nb-option
              *ngFor="let category of categoryOptions"
              [value]="category.key || (category.registration_id ? category.registration_id + ':' + category.id : null)"
            >
              {{ category.label || category.path || category.name || ('pages.moodle.filters.unnamedCategory' | translate) }}
              <span *ngIf="category.count != null" class="muted">({{ category.count }})</span>
            </nb-option>
          </nb-select>

          <!-- Filter: wer fuer den Kurs einsteht. "Ohne Verantwortliche" ist
               der eigentlich interessante Fall — fuer genau diese Kurse kann
               keine Verknuepfung bestaetigt werden. -->
          <nb-select
            size="small"
            placeholder="Verantwortlich"
            [(selected)]="responsibleFilter"
          >
            <nb-option [value]="null">Verantwortlich: alle</nb-option>
            <nb-option value="__none__">Ohne Verantwortliche</nb-option>
            <nb-option *ngFor="let name of responsibleOptions" [value]="name">{{ name }}</nb-option>
          </nb-select>

          <!-- Spalten an/aus, rechts. Gleiches Muster wie im Curriculum. -->
          <button
            nbButton
            size="small"
            class="moodle-dashboard__columns-btn"
            [status]="showColumnPicker ? 'primary' : 'basic'"
            nbTooltip="Sichtbare Spalten wählen"
            (click)="toggleColumnPicker(); $event.stopPropagation()"
          >
            <nb-icon icon="grid-outline"></nb-icon>Spalten
          </button>
        </div>

        <section
          *ngIf="showColumnPicker"
          class="moodle-dashboard__columns-panel"
          (click)="$event.stopPropagation()"
        >
          <span class="moodle-dashboard__columns-label">Sichtbare Spalten</span>
          <div class="moodle-dashboard__columns-list">
            <label *ngFor="let col of courseColumnOptions" class="moodle-dashboard__columns-item">
              <nb-checkbox
                [checked]="isColumnVisible(col.key)"
                (checkedChange)="toggleColumn(col.key)"
              ></nb-checkbox>
              {{ col.label }}
            </label>
          </div>
        </section>

        <div *ngIf="loading" class="moodle-dashboard__loading">
          <nb-spinner status="basic"></nb-spinner>
        </div>

        <div *ngIf="!loading && filteredCourses.length === 0" class="moodle-dashboard__empty">
          {{ coursesEmptyMessageKey | translate }}
        </div>

        <!-- Dieselbe Tabellenkomponente wie in der Wissensbasis: gleiche
             Optik, gleiches Zeilenmenue, und Mehrfachauswahl samt
             Sammelaktionen sind darin schon enthalten. Die handgebaute
             Tabelle davor hatte nichts davon — und ihr eigenes Zeilenmenue
             blieb stumm, weil es Nebulars `nbContextMenu` benutzte statt des
             Hausmusters. Eigene Tabellen sind hier der Fehler, nicht die
             Ausnahme.

             Die Seitenzahlen bleiben die eigenen: sie blaettern serverseitig
             (limit/offset), waehrend der eingebaute Pager nur ueber die schon
             geladene Seite laufen wuerde. -->
        <app-table
          #coursesTable
          *ngIf="!loading && filteredCourses.length > 0"
          rowIdKey="id"
          [title]="'pages.moodle.tabs.courses' | translate"
          [columns]="courseTableColumns"
          [data]="filteredCourses"
          [rowActions]="courseRowActions"
          [selectable]="true"
          [showPagination]="false"
          [maxVisibleItems]="pageSize"
          (event)="onCourseTableEvent($event)"
        ></app-table>

        <app-pagination
          *ngIf="!loading && filteredCourses.length > 0"
          [totalItems]="totalRows.courses"
          [itemsPerPage]="pageSize"
          [currentPage]="currentPage.courses"
          [pageSizeOptions]="pageSizeOptions"
          (pageChanged)="onPageChange('courses', $event)"
          (itemsPerPageChange)="onPageSizeChange($event)"
        ></app-pagination>
      </nb-tab>

      <!-- ─── Tab: Lernende ──────────────────────────────────── -->
      <nb-tab tabId="users" [tabTitle]="'pages.moodle.tabs.users' | translate">
        <nb-form-field class="moodle-dashboard__filter">
          <nb-icon nbPrefix icon="search-outline"></nb-icon>
          <input
            type="text"
            nbInput
            fieldSize="small"
            [(ngModel)]="searchUsersInput"
            (keyup.enter)="applySearch('users')"
            [placeholder]="'pages.moodle.searchUser' | translate"
          />
          <nb-icon
            *ngIf="searchUsersInput"
            nbSuffix
            icon="close-outline"
            class="moodle-dashboard__clear-search"
            (click)="clearSearch('users')"
            [attr.title]="'pages.moodle.clearSearch' | translate"
          ></nb-icon>
        </nb-form-field>

        <div *ngIf="loading" class="moodle-dashboard__loading">
          <nb-spinner status="basic"></nb-spinner>
        </div>

        <div *ngIf="!loading && filteredUsers.length === 0" class="moodle-dashboard__empty">
          {{ usersEmptyMessageKey | translate }}
        </div>

        <table
          *ngIf="!loading && filteredUsers.length > 0"
          class="moodle-dashboard__table moodle-dashboard__table--users"
          [class.is-column-resizing]="isResizingTab('users')"
        >
          <colgroup>
            <col class="col-name" [style.width.%]="columnWidth('users', 'name')" />
            <col class="col-email" [style.width.%]="columnWidth('users', 'email')" />
            <col class="col-id" [style.width.%]="columnWidth('users', 'id')" />
            <col class="col-enrol" [style.width.%]="columnWidth('users', 'enrol')" />
            <col class="col-sync" [style.width.%]="columnWidth('users', 'sync')" />
            <col class="col-action" [style.width.%]="columnWidth('users', 'action')" />
          </colgroup>
          <thead>
            <tr>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.name' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'users', 'name', 'email')"
                  (dblclick)="resetColumnWidths('users')"
                ></span>
              </th>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.email' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'users', 'email', 'id')"
                  (dblclick)="resetColumnWidths('users')"
                ></span>
              </th>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.moodleId' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'users', 'id', 'enrol')"
                  (dblclick)="resetColumnWidths('users')"
                ></span>
              </th>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.enrollments' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'users', 'enrol', 'sync')"
                  (dblclick)="resetColumnWidths('users')"
                ></span>
              </th>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.lastSync' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'users', 'sync', 'action')"
                  (dblclick)="resetColumnWidths('users')"
                ></span>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let u of pagedUsers; trackBy: trackUser"
              class="moodle-dashboard__row"
              (click)="openUser(u)"
            >
              <td class="moodle-dashboard__name moodle-dashboard__cell" [attr.title]="userName(u)">{{ userName(u) }}</td>
              <td class="muted moodle-dashboard__cell" [attr.title]="u.email || null">{{ u.email || '—' }}</td>
              <td class="muted moodle-dashboard__cell">#{{ u.moodle_user_id }}</td>
              <td class="muted moodle-dashboard__cell">{{ u.enrollment_count }}</td>
              <td class="muted moodle-dashboard__cell">{{ formatRelative(u.last_synced_at) }}</td>
              <td class="moodle-dashboard__row-action">
                <button
                  nbButton
                  ghost
                  size="tiny"
                  [disabled]="!u.learner_id"
                  [nbTooltip]="learnerProfileTooltip(u)"
                  nbTooltipPlacement="top"
                  (click)="openLearnerProfile(u, $event)"
                >
                  <nb-icon icon="eye-outline"></nb-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <app-pagination
          *ngIf="!loading && filteredUsers.length > 0"
          [totalItems]="totalRows.users"
          [itemsPerPage]="pageSize"
          [currentPage]="currentPage.users"
          [pageSizeOptions]="pageSizeOptions"
          (pageChanged)="onPageChange('users', $event)"
          (itemsPerPageChange)="onPageSizeChange($event)"
        ></app-pagination>
      </nb-tab>

      <!-- ─── Tab: Moodle-Dozenten ───────────────────────────── -->
      <nb-tab tabId="teachers" [tabTitle]="'pages.moodle.tabs.teachers' | translate">
        <nb-form-field class="moodle-dashboard__filter">
          <nb-icon nbPrefix icon="search-outline"></nb-icon>
          <input
            type="text"
            nbInput
            fieldSize="small"
            [(ngModel)]="searchTeachersInput"
            (keyup.enter)="applySearch('teachers')"
            [placeholder]="'pages.moodle.searchTeacher' | translate"
          />
          <nb-icon
            *ngIf="searchTeachersInput"
            nbSuffix
            icon="close-outline"
            class="moodle-dashboard__clear-search"
            (click)="clearSearch('teachers')"
            [attr.title]="'pages.moodle.clearSearch' | translate"
          ></nb-icon>
        </nb-form-field>

        <div *ngIf="loading" class="moodle-dashboard__loading">
          <nb-spinner status="basic"></nb-spinner>
        </div>

        <div *ngIf="!loading && filteredTeachers.length === 0" class="moodle-dashboard__empty">
          {{ teachersEmptyMessageKey | translate }}
        </div>

        <table
          *ngIf="!loading && filteredTeachers.length > 0"
          class="moodle-dashboard__table moodle-dashboard__table--teachers"
          [class.is-column-resizing]="isResizingTab('teachers')"
        >
          <colgroup>
            <col class="col-name" [style.width.%]="columnWidth('teachers', 'name')" />
            <col class="col-email" [style.width.%]="columnWidth('teachers', 'email')" />
            <col class="col-role" [style.width.%]="columnWidth('teachers', 'role')" />
            <col class="col-enrol" [style.width.%]="columnWidth('teachers', 'enrol')" />
            <col class="col-sync" [style.width.%]="columnWidth('teachers', 'sync')" />
            <col class="col-action-wide" [style.width.%]="columnWidth('teachers', 'actionWide')" />
          </colgroup>
          <thead>
            <tr>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.name' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'teachers', 'name', 'email')"
                  (dblclick)="resetColumnWidths('teachers')"
                ></span>
              </th>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.email' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'teachers', 'email', 'role')"
                  (dblclick)="resetColumnWidths('teachers')"
                ></span>
              </th>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.role' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'teachers', 'role', 'enrol')"
                  (dblclick)="resetColumnWidths('teachers')"
                ></span>
              </th>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.courses' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'teachers', 'enrol', 'sync')"
                  (dblclick)="resetColumnWidths('teachers')"
                ></span>
              </th>
              <th class="moodle-dashboard__resizable-th">
                <span class="moodle-dashboard__th-label">{{ 'pages.moodle.col.lastSync' | translate }}</span>
                <span
                  class="moodle-dashboard__resize-handle"
                  [attr.title]="'pages.moodle.resizeColumn' | translate"
                  (mousedown)="startColumnResize($event, 'teachers', 'sync', 'actionWide')"
                  (dblclick)="resetColumnWidths('teachers')"
                ></span>
              </th>
              <th>{{ 'pages.moodle.col.action' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let u of pagedTeachers; trackBy: trackTeacher">
              <td class="moodle-dashboard__name moodle-dashboard__cell" [attr.title]="teacherName(u)">{{ teacherName(u) }}</td>
              <td class="muted moodle-dashboard__cell" [attr.title]="u.email || null">{{ u.email || '—' }}</td>
              <td class="muted moodle-dashboard__cell" [attr.title]="teacherRoleLabel(u)">{{ teacherRoleLabel(u) }}</td>
              <td class="muted moodle-dashboard__cell">{{ u.course_count }}</td>
              <td class="muted moodle-dashboard__cell">{{ formatRelative(u.last_synced_at) }}</td>
              <td class="moodle-dashboard__row-action">
                <button
                  nbButton
                  ghost
                  size="tiny"
                  [status]="isPortalDozent(u) ? 'success' : 'primary'"
                  [disabled]="!u.email || isPortalDozent(u) || isInvitingTeacher(u) || !featureSets.length"
                  [nbTooltip]="teacherInviteTooltip(u)"
                  nbTooltipPlacement="top"
                  (click)="inviteTeacher(u, $event)"
                >
                  <nb-spinner
                    *ngIf="isInvitingTeacher(u)"
                    size="tiny"
                    status="primary"
                  ></nb-spinner>
                  <nb-icon
                    *ngIf="!isInvitingTeacher(u)"
                    [icon]="isPortalDozent(u) ? 'checkmark-circle-2-outline' : 'person-add-outline'"
                  ></nb-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <app-pagination
          *ngIf="!loading && filteredTeachers.length > 0"
          [totalItems]="totalRows.teachers"
          [itemsPerPage]="pageSize"
          [currentPage]="currentPage.teachers"
          [pageSizeOptions]="pageSizeOptions"
          (pageChanged)="onPageChange('teachers', $event)"
          (itemsPerPageChange)="onPageSizeChange($event)"
        ></app-pagination>
      </nb-tab>
    </nb-tabset>
  </nb-card-body>
</nb-card>
